//This file includes everything needed to call the server's web service API methods. The functions here all start with a word that's like a namespace and then an underscore, which denotes the controller on the server. For example, authentication_ValidateUser will call the API method ValidateUser that is part of the Authentication controller.
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of } from 'rxjs';
import { MessageBoxComponent } from '../message-box/message-box.component';

//For the API URL, we either get it from config.js or we get it passed into the constructor as the baseURL. Currently it's the former, as the latter only works in certain circumstances (see comment below about baseURL).
declare var webApiBaseUrl: string;//This tells the compiler that it's a global variable coming from another file. In our case, it's coming from config.js.

@Injectable({
  providedIn: 'root'
})
export class WebService {
  //Only use the baseURL param if the baseURL for the web API is the same as for the UI (i.e. index.html). The baseURL string passed in comes from main.ts, which grabs the URL from the <base> element in index.html.
  constructor(private http: HttpClient, @Inject('BASE_URL') baseURL: string) {
    //webApiBaseUrl = baseURL;
    if (!webApiBaseUrl)
      alert("config.js not loaded");
  }

  authentication_ValidateUser(userName: string, password: string, nonprofitID: string, timeZoneOffsetMinutes: number, browserGuid: string, passcode: string, registerBrowser: boolean): Observable<ValidateUserResponse> {
    let requestParams = new ValidateUserRequest(userName, password, nonprofitID, timeZoneOffsetMinutes, browserGuid, passcode, registerBrowser);
    return this.http.post<ValidateUserResponse>(webApiBaseUrl + 'api/Authentication/ValidateUser', requestParams);
  }

  authentication_GetMaskedEmail(username: string, nonprofitID: string): Observable<GetStringResponse> {
    let requestParams = new UserNonprofitRequest(username, nonprofitID, '');
    return this.http.post<GetStringResponse>(webApiBaseUrl + 'api/Authentication/GetMaskedEmail', requestParams);
  }

  authentication_SendEmailToResetPassword(username: string, nonprofitID: string, urlBase: string): Observable<ApiResponse> {
    let requestParams = new UserNonprofitRequest(username, nonprofitID, urlBase);
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/SendEmailToResetPassword', requestParams);
  }

  authentication_GetAllUsers(): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/GetAllUsers', null);
  }

  authentication_AddUser(user: User): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/AddUser', user);
  }

  authentication_EditUser(user: User): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/EditUser', user);
  }

  authentication_DeleteUser(userNo: number): Observable<GetUsersResponse> {
    return this.http.post<GetUsersResponse>(webApiBaseUrl + 'api/Authentication/DeleteUser?userNo=' + userNo, null);
  }

  authentication_ChangePasswordWithoutOldPassword(userName: string, newPassword: string, nonprofitID: string, guid: string): Observable<ApiResponse> {
    let requestParams = new ChangePasswordRequest(userName, '', newPassword, nonprofitID);
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/ChangePasswordWithoutOldPassword?guid=' + guid, requestParams);
  }

  authentication_ChangePassword(userName: string, oldPassword: string, newPassword: string, nonprofitID: string): Observable<ApiResponse> {
    let requestParams = new ChangePasswordRequest(userName, oldPassword, newPassword, nonprofitID);
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/ChangePassword', requestParams);
  }

  authentication_EditUserSimple(email: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/EditUserSimple?email=' + email, null);
  }

  authentication_ChangeNonprofitNo(newNonprofitNo: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/ChangeNonprofitNo?newNonprofitNo=' + newNonprofitNo, null);
  }

  authentication_DeleteUserAuth(browserGuid: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Authentication/DeleteUserAuth?browserGuid=' + browserGuid, null);
  }

  //This will send a message to the server that the user is logging out. We use native AJAX support rather than the typcial Anular method because I found that using Angular made the browser pause for a second before navigating to the login page.
  authentication_LogUserLogout() {
    const Http = new XMLHttpRequest();
    const url = webApiBaseUrl + 'api/Authentication/LogUserLogout?userNo=' + sessionStorage.userNo;
    Http.open("GET", url);
    Http.send();
  }

  contributions_GetDeposits(request: GetContributionDepositsRequest): Observable<GetContributionDepositsResponse> {
    return this.http.post<GetContributionDepositsResponse>(webApiBaseUrl + 'api/Contributions/GetDeposits', request);
  }
  
  contributions_EditDeposit(contTypeNo: number, contDepositNo: number, depositDate: string, glPostingDate: string, depositAmount: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Contributions/EditDeposit?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo + '&depositDate=' + depositDate + '&glPostingDate=' + glPostingDate + '&depositAmount=' + depositAmount, null);
  }

  contributions_PostDeposit(contTypeNo: number, contDepositNo: number): Observable<GetContributionsAndJournalsResponse> {
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/PostDeposit?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo, null);
  }

  contributions_PostDepositAlone(contTypeNo: number, contNo: number, filter: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/PostDepositAlone?contTypeNo=' + contTypeNo + '&contNo=' + contNo + '&filterSerialized=' + JSON.stringify(filter), null);
  }

  contributions_GetContributionsAndJournals(contTypeNo: number, contDepositNo: number): Observable<GetContributionsAndJournalsResponse> {
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/GetContributionsAndJournals?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo, null);
  }

  contributions_GetContributionsWithDesignations(contTypeNo: number, contDepositNo: number): Observable<GetContributionsWithDesignationsResponse> {
    return this.http.post<GetContributionsWithDesignationsResponse>(webApiBaseUrl + 'api/Contributions/GetContributionsWithDesignations?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo, null);
  }

  contributions_GetContributionsAlone(contTypeNo: number, request: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/GetContributionsAlone?contTypeNo=' + contTypeNo, request);
  }
  
  contributions_GetContributionDetailsCash(contCashNo: number): Observable<GetContributionDetailsResponse> {
    return this.http.post<GetContributionDetailsResponse>(webApiBaseUrl + 'api/Contributions/GetContributionDetailsCash?contCashNo=' + contCashNo, null);
  }

  contributions_AddEditContributionCash(contDepositNo: number, cont: ContributionCash, desChanged: boolean, giftsProvidedChanged: boolean, docsChanged: boolean, depositDate: string, glPostingDate: string, depositAmount: string): Observable<GetContributionsAndJournalsResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being edited. But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    formData.append('ContributionCash', JSON.stringify(cont));
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    for (let i = 0; i < cont.docsList.length; i++) {
      if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
        formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
      else
        formData.append('file' + i, new Blob([]), "");
    }
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/AddEditContributionCash?contDepositNo=' + contDepositNo + '&desChanged=' + desChanged + '&giftsProvidedChanged=' + giftsProvidedChanged + '&docsChanged=' + docsChanged + '&depositDate=' + depositDate + '&glPostingDate=' + glPostingDate + '&depositAmount=' + depositAmount, formData);
  }

  contributions_AdjustContributionCash(contTypeNo: number, contDepositNo: number, contNo: number, adjustedReplace: boolean, adjustedReason: string, adjustedComment: string, cont: ContributionCash, voidDate: string): Observable<GetContributionsAndJournalsResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being adjusted (if it's being replaced). But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    if (cont != null) {
      formData.append('ReplacementCont', JSON.stringify(cont));
      for (let i = 0; i < cont.docsList.length; i++) {
        if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
          formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
        else
          formData.append('file' + i, new Blob([]), "");
      }
    }
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/AdjustContributionCash?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo + '&contNo=' + contNo + '&adjustedReplace=' + adjustedReplace + '&adjustedReason=' + adjustedReason + '&adjustedComment=' + encodeURIComponent(adjustedComment) + '&voidDate=' + voidDate, formData);
  }

  contributions_ImportContributionsCash(contArr: ContributionCash[]): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(webApiBaseUrl + 'api/Contributions/ImportContributionsCash', contArr);
  }

  contributions_GetContributionDetailsCredit(contCreditNo: number): Observable<GetContributionDetailsResponse> {
    return this.http.post<GetContributionDetailsResponse>(webApiBaseUrl + 'api/Contributions/GetContributionDetailsCredit?contCreditNo=' + contCreditNo, null);
  }

  contributions_AddEditContributionCredit(contDepositNo: number, cont: ContributionCredit, desChanged: boolean, giftsProvidedChanged: boolean, docsChanged: boolean, depositDate: string, depositAmount: string): Observable<GetContributionsAndJournalsResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being edited. But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    formData.append('ContributionCredit', JSON.stringify(cont));
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    for (let i = 0; i < cont.docsList.length; i++) {
      if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
        formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
      else
        formData.append('file' + i, new Blob([]), "");
    }
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/AddEditContributionCredit?contDepositNo=' + contDepositNo + '&desChanged=' + desChanged + '&giftsProvidedChanged=' + giftsProvidedChanged + '&docsChanged=' + docsChanged + '&depositDate=' + depositDate + '&depositAmount=' + depositAmount, formData);
  }

  contributions_AdjustContributionCredit(contTypeNo: number, contDepositNo: number, contNo: number, adjustedReplace: boolean, adjustedReason: string, adjustedComment: string, cont: ContributionCredit, voidDate: string): Observable<GetContributionsAndJournalsResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being adjusted (if it's being replaced). But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    if (cont != null) {
      formData.append('ReplacementCont', JSON.stringify(cont));
      for (let i = 0; i < cont.docsList.length; i++) {
        if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
          formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
        else
          formData.append('file' + i, new Blob([]), "");
      }
    }
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/AdjustContributionCredit?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo + '&contNo=' + contNo + '&adjustedReplace=' + adjustedReplace + '&adjustedReason=' + adjustedReason + '&adjustedComment=' + encodeURIComponent(adjustedComment) + '&voidDate=' + voidDate, formData);
  }

  contributions_GetContributionDetailsTransfer(contTransferNo: number): Observable<GetContributionDetailsResponse> {
    return this.http.post<GetContributionDetailsResponse>(webApiBaseUrl + 'api/Contributions/GetContributionDetailsTransfer?contTransferNo=' + contTransferNo, null);
  }

  contributions_AddEditContributionTransfer(contDepositNo: number, cont: ContributionTransfer, desChanged: boolean, giftsProvidedChanged: boolean, docsChanged: boolean, depositDate: string, depositAmount: string): Observable<GetContributionsAndJournalsResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being edited. But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    formData.append('ContributionTransfer', JSON.stringify(cont));
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    for (let i = 0; i < cont.docsList.length; i++) {
      if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
        formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
      else
        formData.append('file' + i, new Blob([]), "");
    }
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/AddEditContributionTransfer?contDepositNo=' + contDepositNo + '&desChanged=' + desChanged + '&giftsProvidedChanged=' + giftsProvidedChanged + '&docsChanged=' + docsChanged + '&depositDate=' + depositDate + '&depositAmount=' + depositAmount, formData);
  }

  contributions_AdjustContributionTransfer(contTypeNo: number, contDepositNo: number, contNo: number, adjustedReplace: boolean, adjustedReason: string, adjustedComment: string, cont: ContributionTransfer, voidDate: string): Observable<GetContributionsAndJournalsResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being adjusted (if it's being replaced). But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    if (cont != null) {
      formData.append('ReplacementCont', JSON.stringify(cont));
      for (let i = 0; i < cont.docsList.length; i++) {
        if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
          formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
        else
          formData.append('file' + i, new Blob([]), "");
      }
    }
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/AdjustContributionTransfer?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo + '&contNo=' + contNo + '&adjustedReplace=' + adjustedReplace + '&adjustedReason=' + adjustedReason + '&adjustedComment=' + encodeURIComponent(adjustedComment) + '&voidDate=' + voidDate, formData);
  }

  contributions_GetContributionDetailsSecurities(contSecuritiesNo: number): Observable<GetContributionDetailsResponse> {
    return this.http.post<GetContributionDetailsResponse>(webApiBaseUrl + 'api/Contributions/GetContributionDetailsSecurities?contSecuritiesNo=' + contSecuritiesNo, null);
  }

  contributions_AddEditContributionSecurities(cont: ContributionSecurities, desChanged: boolean, giftsProvidedChanged: boolean, docsChanged: boolean, filter: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being edited. But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    formData.append('ContributionSecurities', JSON.stringify(cont));
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    for (let i = 0; i < cont.docsList.length; i++) {
      if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
        formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
      else
        formData.append('file' + i, new Blob([]), "");
    }
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/AddEditContributionSecurities?desChanged=' + desChanged + '&giftsProvidedChanged=' + giftsProvidedChanged + '&docsChanged=' + docsChanged + '&filterSerialized=' + JSON.stringify(filter), formData);
  }

  contributions_AdjustContributionSecurities(contTypeNo: number, contNo: number, adjustedReplace: boolean, adjustedComment: string, cont: ContributionSecurities, filter: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being adjusted (if it's being replaced). But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    if (cont != null) {
      formData.append('ReplacementCont', JSON.stringify(cont));
      for (let i = 0; i < cont.docsList.length; i++) {
        if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
          formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
        else
          formData.append('file' + i, new Blob([]), "");
      }
    }
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/AdjustContributionSecurities?contTypeNo=' + contTypeNo + '&contNo=' + contNo + '&adjustedReplace=' + adjustedReplace + '&adjustedComment=' + encodeURIComponent(adjustedComment) + '&filterSerialized=' + JSON.stringify(filter), formData);
  }

  contributions_GetContributionDetailsNoncash(contNoncashNo: number): Observable<GetContributionDetailsResponse> {
    return this.http.post<GetContributionDetailsResponse>(webApiBaseUrl + 'api/Contributions/GetContributionDetailsNoncash?contNoncashNo=' + contNoncashNo, null);
  }

  contributions_AddEditContributionNoncash(cont: ContributionNoncash, desChanged: boolean, giftsProvidedChanged: boolean, docsChanged: boolean, filter: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being edited. But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    formData.append('ContributionNoncash', JSON.stringify(cont));
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    for (let i = 0; i < cont.docsList.length; i++) {
      if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
        formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
      else
        formData.append('file' + i, new Blob([]), "");
    }
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/AddEditContributionNoncash?desChanged=' + desChanged + '&giftsProvidedChanged=' + giftsProvidedChanged + '&docsChanged=' + docsChanged + '&filterSerialized=' + JSON.stringify(filter), formData);
  }

  contributions_AdjustContributionNoncash(contTypeNo: number, contNo: number, adjustedReplace: boolean, adjustedComment: string, cont: ContributionNoncash, filter: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the C being adjusted (if it's being replaced). But we can only pass the file contents in a FormData object, so we serialize the C and pass it as one of the params of the FormData
    let formData = new FormData();
    //We need to put a file in the list for every doc, but for ones that haven't changed (i.e. we are not uploading a new document), put a blank array for the doc
    if (cont != null) {
      formData.append('ReplacementCont', JSON.stringify(cont));
      for (let i = 0; i < cont.docsList.length; i++) {
        if (cont.docsList[i].newFile && cont.docsList[i].newFile.size > 0)
          formData.append('file' + i, cont.docsList[i].newFile, cont.docsList[i].newFile.name);
        else
          formData.append('file' + i, new Blob([]), "");
      }
    }
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/AdjustContributionNoncash?contTypeNo=' + contTypeNo + '&contNo=' + contNo + '&adjustedReplace=' + adjustedReplace + '&adjustedComment=' + encodeURIComponent(adjustedComment) + '&filterSerialized=' + JSON.stringify(filter), formData);
  }

  contributions_DeleteContribution(contTypeNo: number, contDepositNo: number, contNo: number): Observable<GetContributionsAndJournalsResponse> {
    return this.http.post<GetContributionsAndJournalsResponse>(webApiBaseUrl + 'api/Contributions/DeleteContribution?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo + '&contNo=' + contNo, null);
  }

  contributions_DeleteContributionAlone(contTypeNo: number, contNo: number, filter: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/DeleteContributionAlone?contTypeNo=' + contTypeNo + '&contNo=' + contNo + '&filterSerialized=' + JSON.stringify(filter), null);
  }

  contributions_UndisposeContribution(contTypeNo: number, contNo: number, filter: GetContributionDepositsRequest): Observable<GetContributionsAloneResponse> {
    return this.http.post<GetContributionsAloneResponse>(webApiBaseUrl + 'api/Contributions/UndisposeContribution?contTypeNo=' + contTypeNo + '&contNo=' + contNo + '&filterSerialized=' + JSON.stringify(filter), null);
  }

  contributions_GetJournalEntryDetails(contTypeNo: number, contDepositNo: number, journalNo: number): Observable<GetContributionJournalEntryDetailsResponse> {
    return this.http.post<GetContributionJournalEntryDetailsResponse>(webApiBaseUrl + 'api/Contributions/GetJournalEntryDetails?contTypeNo=' + contTypeNo + '&contDepositNo=' + contDepositNo + '&journalNo=' + journalNo, null);
  }

  pledges_GetAllPledges(filter: FilterPledges): Observable<GetPledgesResponse> {
    return this.http.post<GetPledgesResponse>(webApiBaseUrl + 'api/Pledges/GetAllPledges?fromDate=' + filter.fromDate + '&toDate=' + filter.toDate + '&desNo=' + filter.desNo + '&donorName=' + encodeURIComponent(filter.donorName), null);
  }

  pledges_GetPledgeInfoByDonor(donorNo: number): Observable<GetPledgeInfoByDonorResponse> {
    return this.http.post<GetPledgeInfoByDonorResponse>(webApiBaseUrl + 'api/Pledges/GetPledgeInfoByDonor?donorNo=' + donorNo, null);
  }

  pledges_AddEditPledge(pledge: Pledge, filter: FilterPledges): Observable<GetPledgesResponse> {
    return this.http.post<GetPledgesResponse>(webApiBaseUrl + 'api/Pledges/AddEditPledge?fromDate=' + filter.fromDate + '&toDate=' + filter.toDate + '&desNo=' + filter.desNo + '&donorName=' + encodeURIComponent(filter.donorName), pledge);
  }

  pledges_DeletePledge(pledgeNo: number, filter: FilterPledges): Observable<GetPledgesResponse> {
    return this.http.post<GetPledgesResponse>(webApiBaseUrl + 'api/Pledges/DeletePledge?pledgeNo=' + pledgeNo + '&fromDate=' + filter.fromDate + '&toDate=' + filter.toDate + '&desNo=' + filter.desNo + '&donorName=' + encodeURIComponent(filter.donorName), null);
  }

  options_GetRoleOptions(): Observable<GetSimpleOptionsResponse> {
    return this.http.post<GetSimpleOptionsResponse>(webApiBaseUrl + 'api/Options/GetRoleOptions', null);
  }

  options_GetAllOptions(optionTypeNo: number, onlyGetActive: boolean): Observable<GetOptionsResponse> {
    return this.http.post<GetOptionsResponse>(webApiBaseUrl + 'api/Options/GetAllOptions?optionTypeNo=' + optionTypeNo + '&onlyGetActive=' + onlyGetActive, null);
  }

  options_AddEditOption(option: Option, extraInfoMustBeUnique: boolean): Observable<GetOptionsResponse> {
    return this.http.post<GetOptionsResponse>(webApiBaseUrl + 'api/Options/AddEditOption?extraInfoMustBeUnique=' + extraInfoMustBeUnique, option);
  }
  
  options_DeleteOption(optionNo: number, optionTypeNo: number, onlyGetActive: boolean): Observable<GetOptionsResponse> {
    return this.http.post<GetOptionsResponse>(webApiBaseUrl + `api/Options/DeleteOption?optionNo=${optionNo}&optionTypeNo=${optionTypeNo}&onlyGetActive=${onlyGetActive}`, null);
  }

  options_GetAllReceivingAccounts(): Observable<GetReceivingAccountsResponse> {
    return this.http.post<GetReceivingAccountsResponse>(webApiBaseUrl + 'api/Options/GetAllReceivingAccounts', null);
  }

  options_AddEditReceivingAccounts(accounts: ReceivingAccount[]): Observable<GetReceivingAccountsResponse> {
    return this.http.post<GetReceivingAccountsResponse>(webApiBaseUrl + 'api/Options/AddEditReceivingAccounts', accounts);
  }
  
  designations_GetAllDesignations(onlyGetActive: boolean, onlyWithConfigsActive: boolean, onlyWithConfigsActiveAndAllowPledge: boolean): Observable<GetDesignationsResponse> {
    return this.http.post<GetDesignationsResponse>(webApiBaseUrl + `api/Designations/GetAllDesignations?onlyGetActive=${onlyGetActive}&onlyWithConfigsActive=${onlyWithConfigsActive}&onlyWithConfigsActiveAndAllowPledge=${onlyWithConfigsActiveAndAllowPledge}`, null);
  }

  designations_GetDesignationDetails(desNo: number): Observable<GetDesignationDetailsResponse> {
    return this.http.post<GetDesignationDetailsResponse>(webApiBaseUrl + 'api/Designations/GetDesignationDetails?desNo=' + desNo, null);
  }

  designations_AddEditDesignationDetails(details: DesignationDetails): Observable<GetDesignationsResponse> {
    return this.http.post<GetDesignationsResponse>(webApiBaseUrl + 'api/Designations/AddEditDesignationDetails', details);
  }

  designations_DeleteDesignation(desNo: number): Observable<GetDesignationsResponse> {
    return this.http.post<GetDesignationsResponse>(webApiBaseUrl + 'api/Designations/DeleteDesignation?desNo=' + desNo, null);
  }

  designations_GetAllDesignationConfigs(): Observable<GetSimpleOptionsResponse> {
    return this.http.post<GetSimpleOptionsResponse>(webApiBaseUrl + 'api/Designations/GetAllDesignationConfigs', null);
  }

  donors_GetAllDonorNames(onlyGetActive: boolean = true): Observable<GetSimpleOptionsResponse> {
    return this.http.post<GetSimpleOptionsResponse>(webApiBaseUrl + 'api/Donors/GetAllDonorNames?onlyGetActive=' + onlyGetActive, null);
  }

  donors_GetAllDonors(onlyGetActive: boolean): Observable<GetDonorsResponse> {
    return this.http.post<GetDonorsResponse>(webApiBaseUrl + 'api/Donors/GetAllDonors?onlyGetActive=' + onlyGetActive, null);
  }

  donors_AddEditDonor(donor: Donor): Observable<GetDonorsResponse> {
    return this.http.post<GetDonorsResponse>(webApiBaseUrl + 'api/Donors/AddEditDonor', donor);
  }

  donors_ImportDonors(donorArr: Donor[]): Observable<GetStringResponse> {
    return this.http.post<GetStringResponse>(webApiBaseUrl + 'api/Donors/ImportDonors', donorArr);
  }

  statements_GetSettings(includeNonprofitAddress: boolean): Observable<GetStringResponse> {
    return this.http.post<GetStringResponse>(webApiBaseUrl + `api/Statements/GetSettings?includeNonprofitAddress=${includeNonprofitAddress}`, null);
  }

  statements_EditSettings(settings: object, newImageFiles: object): Observable<GetStringResponse> {
    //This one is a bit weird because we want to pass the file contents of documents in addition to the settings. But we can only pass the file contents in a FormData object, so we serialize the settings and pass it as one of the params of the FormData.
    let formData = new FormData();
    formData.append('Settings', JSON.stringify(settings));
    //We need to put a file in the list for every uploaded image file. newImageFiles is used like a dictionary or hash table and contains the File objects.
    for (var prop in newImageFiles) {
      let file: File = <File>newImageFiles[prop];
      formData.append(prop, file, file.name);
    }
    return this.http.post<GetStringResponse>(webApiBaseUrl + `api/Statements/EditSettings`, formData);
  }

  statements_GetDonors(filter: FilterStatements): Observable<GetStatementDonorsResponse> {
    return this.http.post<GetStatementDonorsResponse>(webApiBaseUrl + `api/Statements/GetDonors?periodNo=${filter.periodNo}&year=${filter.year}&fromDate=${filter.fromDate}&toDate=${filter.toDate}&donorName=${encodeURIComponent(filter.donorName)}&statementPaperEmail=${filter.statementPaperEmail}&showDonorsWithNoContInDateRange=${filter.showDonorsWithNoContInDateRange}`, null);
  }

  statements_EmailAllStatements(filter: FilterStatements, donorNo: number, include8283: boolean, emailSubject: string, emailBodyText: string): Observable<ApiResponse> {
    let urlBase: string = document.getElementsByTagName('base')[0].href;
    return this.http.post<ApiResponse>(webApiBaseUrl + `api/Statements/EmailAllStatements?periodNo=${filter.periodNo}&year=${filter.year}&fromDate=${filter.fromDate}&toDate=${filter.toDate}&donorName=${encodeURIComponent(filter.donorName)}&donorNo=${donorNo}&statementPaperEmail=${filter.statementPaperEmail}&include8283=${include8283}&emailSubject=${encodeURIComponent(emailSubject)}&emailBodyText=${encodeURIComponent(emailBodyText)}&urlBase=${encodeURIComponent(urlBase)}`, null);
  }

  statements_GetEmailedStatementUrl(zipCode: string, doc: string, id: string): Observable<GetStringResponse> {
    return this.http.post<GetStringResponse>(webApiBaseUrl + `api/Statements/GetEmailedStatementUrl?zipCode=${encodeURIComponent(zipCode)}&doc=${doc}&id=${id}`, null);
  }

  static statements_GetImageUrl(fileName: string): string {
    return webApiBaseUrl + `api/Statements/GetImage?fileName=${encodeURIComponent(fileName)}&token=${encodeURIComponent(sessionStorage.validationToken)}`;
  }

  static statements_GetStatementUrl(filter: FilterStatements, donorNo: number): string {
    return webApiBaseUrl + `api/Statements/GetStatement?periodNo=${filter.periodNo}&year=${filter.year}&fromDate=${filter.fromDate}&toDate=${filter.toDate}&donorNo=${donorNo}&token=${encodeURIComponent(sessionStorage.validationToken)}`;
  }

  static statements_GetAllStatementsUrl(filter: FilterStatements, zipStatements: boolean, include8283: boolean, responseContentsInline: boolean): string {
    return webApiBaseUrl + `api/Statements/GetAllStatements?periodNo=${filter.periodNo}&year=${filter.year}&fromDate=${filter.fromDate}&toDate=${filter.toDate}&donorName=${encodeURIComponent(filter.donorName)}&showDonorsWithNoContInDateRange=${filter.showDonorsWithNoContInDateRange}&statementPaperEmail=${filter.statementPaperEmail}&zipStatements=${zipStatements}&include8283=${include8283}&responseContentsInline=${responseContentsInline}&token=${encodeURIComponent(sessionStorage.validationToken)}`;
  }

  static statements_Get8282Url(contNoncashNo: number): string {
    return webApiBaseUrl + `api/Statements/Get8282?contNoncashNo=${contNoncashNo}&token=${encodeURIComponent(sessionStorage.validationToken)}`;
  }

  reports_GetGivingData(filter: ReportFavorite): Observable<GivingReportDataResponse> {
    return this.http.post<GivingReportDataResponse>(webApiBaseUrl + `api/Reports/GetGivingData?periodNo=${filter.periodNo}&year=${filter.reportYear}&fromDate=${filter.fromDate}&toDate=${filter.toDate}&dateGroupByNo=${filter.dateGroupByNo}&desGroupByNo=${filter.desGroupByNos[0]}&desNo=${filter.desNo}&subDes1No=${filter.subDes1No}&subDes2No=${filter.subDes2No}&subDes3No=${filter.subDes3No}`, null);
  }

  reports_GetDevSummary(filter: ReportFavorite): Observable<DevReportSummaryResponse> {
    return this.http.post<DevReportSummaryResponse>(webApiBaseUrl + `api/Reports/GetDevSummary`, filter);
  }

  reports_GetDevDetails(filter: ReportFavorite): Observable<DevReportDetailsResponse> {
    return this.http.post<DevReportDetailsResponse>(webApiBaseUrl + `api/Reports/GetDevDetails`, filter);
  }

  reports_GetPledgesSummary(filter: ReportFavorite): Observable<PledgesReportSummaryResponse> {
    return this.http.post<PledgesReportSummaryResponse>(webApiBaseUrl + `api/Reports/GetPledgesSummary`, filter);
  }

  reports_GetPledgesDetails(filter: ReportFavorite): Observable<PledgesReportDetailsResponse> {
    return this.http.post<PledgesReportDetailsResponse>(webApiBaseUrl + `api/Reports/GetPledgesDetails`, filter);
  }

  reports_ValidateAdvancedFilter(filterValue: string): Observable<GetStringResponse> {
    //We want to pass the string as the body of the request, so we have to convert the string to JSON (which I think will just put it inside quotes and escape anything necessary) and change the content-type to application/json
    return this.http.post<GetStringResponse>(webApiBaseUrl + `api/Reports/ValidateAdvancedFilter`, JSON.stringify(filterValue),
      { headers: new HttpHeaders().set('Content-Type', 'application/json') });
  }

  reports_GetAllReportFavorites(reportTypeNo: number): Observable<GetReportFavoritesResponse> {
    return this.http.post<GetReportFavoritesResponse>(webApiBaseUrl + 'api/Reports/GetAllReportFavorites?reportTypeNo=' + reportTypeNo, null);
  }

  reports_AddEditReportFavorite(reportFavorite: ReportFavorite): Observable<GetReportFavoritesResponse> {
    return this.http.post<GetReportFavoritesResponse>(webApiBaseUrl + 'api/Reports/AddEditReportFavorite', reportFavorite);
  }

  reports_DeleteReportFavorite(reportFavoriteNo: number): Observable<GetReportFavoritesResponse> {
    return this.http.post<GetReportFavoritesResponse>(webApiBaseUrl + 'api/Reports/DeleteReportFavorite?reportFavoriteNo=' + reportFavoriteNo, null);
  }

  //This helper function is for the reports_GetGivingData() function. The various values passed in for the des and sub-des choices (in the filter) might be negative or 0, which actually means to ignore them, so we convert them to null if they are negative or 0. This is a weird behavior of the designation-chooser control.
  checkNegative(i: number): number {
    return (i <= 0 ? null : i);
  }

  trainings_GetAllTrainings(): Observable<GetTrainingsResponse> {
    return this.http.post<GetTrainingsResponse>(webApiBaseUrl + 'api/Trainings/GetAllTrainings', null);
  }

  trainings_AddEditTraining(training: Training): Observable<GetTrainingsResponse> {
    return this.http.post<GetTrainingsResponse>(webApiBaseUrl + 'api/Trainings/AddEditTraining', training);
  }

  trainings_DeleteTraining(trainingNo: number): Observable<GetTrainingsResponse> {
    return this.http.post<GetTrainingsResponse>(webApiBaseUrl + 'api/Trainings/DeleteTraining?trainingNo=' + trainingNo, null);
  }

  nonprofits_GetAllNonprofits(): Observable<GetNonprofitsResponse> {
    return this.http.post<GetNonprofitsResponse>(webApiBaseUrl + 'api/Nonprofits/GetAllNonprofits?returnSimpleListToo=false', null);
  }

  nonprofits_AddEditNonprofit(np: Nonprofit): Observable<GetNonprofitsResponse> {
    return this.http.post<GetNonprofitsResponse>(webApiBaseUrl + 'api/Nonprofits/AddEditNonprofit', np);
  }
}

//base class for response objects
export class ApiResponse {
  errorMessage: string;
  errorCode: number;

  static checkStatus(resp: ApiResponse): boolean {
    if (resp.errorCode != 0) {
      if (MessageBoxComponent.staticInstance)
        MessageBoxComponent.staticInstance.show("Error", resp.errorMessage, false);
      return false;
    }
    return true;
  }
}

export class ValidateUserResponse extends ApiResponse {
  userValidated: boolean; //this is passed back from the server, but we don't actually use it because we are able to tell from other fields if the user is validated.
  browserRegistered: boolean;
  passwordExpired: boolean;
  enableDonorEnvelope: boolean;
  userName: string;
  userNo: number;
  firstName: string;
  lastName: string;
  nonprofitName: string;
  validationToken: string;
  browserGuid: string;
  tokenExpirationSeconds: number;
  authorityRoles: string[];
  nonprofitList: SimpleOption[];
}

export class ValidateUserRequest {
  constructor( //These params will automatically become the properties of this class
    public userName: string,
    public password: string,
    public nonprofitID: string,
    public TimeZoneOffsetMinutes: number,
    public browserGuid: string,
    public passcode: string,
    public registerBrowser: boolean
  ) { };
}

export class ChangePasswordRequest {
  constructor( //These params will automatically become the properties of this class
    public username: string,
    public oldPassword: string,
    public newPassword: string,
    public nonprofitID: string
  ) { };
}

export class UserNonprofitRequest {
  constructor( //These params will automatically become the properties of this class
    public username: string,
    public nonprofitID: string,
    public urlBase: string
  ) { };
}

export class User {
  constructor( //These params will automatically become the properties of this class
    public userNo: number,
    public userName: string,
    public firstName: string,
    public lastName: string,
    public email: string,
    public phone: string,
    public userActive: boolean,
    public isLockedOut: boolean,
    public lastLogin: Date,
    public userRoles: string,
    public password: string
  ) { };

  static create() {
    return new User(0, '', '', '', '', '', true, false, null, '', '');
  }
}

export class GetUsersResponse extends ApiResponse {
  public users: User[];
}

export class GetStringResponse extends ApiResponse {
  public message: string;
}

export class ContributionDeposit {
  constructor( //These params will automatically become the properties of this class
    public contDepositNo: number,
    public depositNo: number,
    public depositDate: string,
    public glPostingDate: string,
    public depositAmount: string,
    public totalContributions: string,
    public bankAdjustments: string,
    public recordNos: string,
    public posted: boolean
  ) { };
}

export class GetContributionDepositsRequest {
  constructor( //These params will automatically become the properties of this class
    public contTypeNo: number,
    public fromDate: string,
    public toDate: string,
    public depositNo: string,
    public recordNo: string,
    public posted: string
  ) { };
}

export class GetContributionDepositsResponse extends ApiResponse {
  public depositList: ContributionDeposit[];
}

export class Contribution { //This only has the fields necessary to show in the grid. Each cont type has more fields when adding/editing.
  public contNo: number;
  public recordNo: number;
  public contDate: string;
  public donorName: string;
  public envelope: string;
  public extraInfo: string;
  public contAmount: string;
  public adjustedReplace: any; //this is sent as a bool, but we have reasons to handle as a string
  public replaceAdjustedContNo: number;
  public adjustedReason: string;
  public adjustedComment: string;
  public voidDate: string;
}

export class ContributionAlone extends Contribution {
  public posted: boolean;
  public journalTime: string;
  public journalNo: number;
  public disposeDate: string;
}

export class ContributionDocument {
  constructor( //These params will automatically become the properties of this class
    public contDocNo: number,
    public originalFileName: string,
    public docName: string,
    public docDesc: string,
    public docUrl: string,
    public newFile: File,
  ) { };
}

export class GetContributionsAndJournalsResponse extends ApiResponse {
  public extraInfo: any;
  public contributionList: Contribution[];
  public journalList: ContributionJournalEntry[];
}

export class GetContributionsAloneResponse extends ApiResponse {
  public contributionList: ContributionAlone[];
}

export class GetContributionDetailsResponse extends ApiResponse {
  public contribution: Contribution;
}

export class ContributionJournalEntryDetail {
  constructor( //These params will automatically become the properties of this class
    public accountName: string,
    public accountNumber: string,
    public debitAmount: string,
    public creditAmount: string
  ) { };
}

export class ContributionJournalEntry {
  public journalNo: number;
  public journalTime: string;
  public voidedAmount: string;
  public voidDate: string;
}

export class GetContributionJournalEntryDetailsResponse extends ApiResponse {
  public journalList: ContributionJournalEntryDetail[];
}

export class ContributionCash extends Contribution {
  public contDepositNo: number;
  public donorNo: number;
  public giftNote: string;
  public giftsProvidedNos: number[];
  public desList: ContributionDesignation[];
  public docsList: ContributionDocument[];
  public giftTypeNo: number;
  public checkNumber: string;
}

export class ContributionCredit extends Contribution {
  public contDepositNo: number;
  public donorNo: number;
  public giftNote: string;
  public giftsProvidedNos: number[];
  public desList: ContributionDesignation[];
  public docsList: ContributionDocument[];
}

export class ContributionTransfer extends Contribution {
  public contDepositNo: number;
  public donorNo: number;
  public giftNote: string;
  public giftsProvidedNos: number[];
  public desList: ContributionDesignation[];
  public docsList: ContributionDocument[];
}

export class ContributionSecurities extends ContributionAlone {
  public donorNo: number;
  public securityDesc: string;
  public brokerName: string;
  public numberShares: number;
  public shareValueMin: string;
  public shareValueMax: string;
  public shareValueOverride: boolean;
  public shareValue: string;
  public stockSymbol: string;
  public intendedUse: string;
  public giftsProvidedNos: number[];
  public desList: ContributionDesignation[];
  public docsList: ContributionDocument[];
}

export class ContributionNoncash extends ContributionAlone {
  public donorNo: number;
  public noncashDesc: string;
  public noncashCondition: string;
  public howDetermineFMV: string;
  public isCapitalExpense: boolean;
  public dontNeedSSN: boolean;
  public donorSSN: string;
  public noncashCategory: string;
  public willRelateToExemptPurpose: boolean;
  public intendedUse: string;
  public actualUse: string;
  public didInvolveEntireInterest: boolean;
  public didRelateToExemptPurpose: boolean;
  public howRelateToExemptPurpose: string;
  public becameImpossibleToImplement: boolean;
  public disposeAmount: string;
  public donatedToOther: boolean;
  public otherName: string;
  public otherEIN: string;
  public otherAddress: string;
  public otherCity: string;
  public otherStateOptionNo: string;
  public otherZipCode: string;
  public disposeDesc: string;
  public giftsProvidedNos: number[];
  public desList: ContributionDesignation[];
  public docsList: ContributionDocument[];
}

export class ContributionWithDesignations {
  public recordNo: number;
  public donorName: string;
  public giftType: string;
  public checkNumber: string;
  public contAmount: string;
  public giftsProvided: string;
  public desName: string;
  public subDes1: string;
  public subDes2: string;
  public subDes3: string;
  public desAmount: string;
  public fulfillsPledge: boolean;
}

export class GetContributionsWithDesignationsResponse extends ApiResponse {
  public contributionList: ContributionWithDesignations[];
}

export class Pledge {
  constructor( //These params will automatically become the properties of this class
    public pledgeNo: number,
    public donorNo: number,
    public desConfigNo: number,
    public pledgeAmount: string,
    public createdTime: Date,
    public pledgeActive: boolean,
    public donorName: string,
    public desNo: number,
    public desName: string,
    public subItemNo1: number,
    public subItemNo2: number,
    public subItemNo3: number,
    public subDes1: string,
    public subDes2: string,
    public subDes3: string,
  ) { };
}

export class GetPledgesResponse extends ApiResponse {
  public pledgeList: Pledge[];
}

export class GetPledgeInfoByDonorResponse extends ApiResponse {
  public pledgeInfoList: number[][];
}

export class SimpleOption {
  constructor( //These params will automatically become the properties of this class
    public optionId: string,
    public optionName: string,
  ) { };
}

export class GetSimpleOptionsResponse extends ApiResponse {
  optionList: SimpleOption[];
}

export class Option {
  constructor( //These params will automatically become the properties of this class
    public optionNo: number,
    public optionTypeNo: number,
    public optionName: string,
    public optionDesc: string,
    public extraInfo: string,
    public optionActive: boolean,
  ) { };
}

export class GetOptionsResponse extends ApiResponse {
  public optionList: Option[];
}

export class ReceivingAccount {
  constructor( //These params will automatically become the properties of this class
    public receivingAccountNo: number,
    public contTypeNo: number,
    public ledgerOptionNo: number,
    public ledgerName: string,
    public accountTypeName: string, //this field isn't passed back from the server; we fill it in ourselves.
  ) { };
}

export class GetReceivingAccountsResponse extends ApiResponse {
  public accountList: ReceivingAccount[];
}

export class Designation {
  constructor( //These params will automatically become the properties of this class
    public desNo: number,
    public desName: string,
    public subName1: string,
    public subName2: string,
    public subName3: string,
    public desActive: boolean,
    public subPrintOnStatement1: boolean,
    public subPrintOnStatement2: boolean,
    public subPrintOnStatement3: boolean,
    public printPledgeAmount: boolean,
    public printTotalPledgeGifts: boolean,
    public printPledgeRemaining: boolean,
  ) { };
}

export class DesignationSubItem {
  constructor( //These params will automatically become the properties of this class
    public subItemNo: number,
    public desNo: number,
    public subLevel: number,
    public itemValue: string,
    public orderNo: number,
  ) { };
}

export class DesignationConfig {
  constructor( //These params will automatically become the properties of this class
    public desConfigNo: number,
    public subItemNo1: number,
    public subItemNo2: number,
    public subItemNo3: number,
    public allowPledge: boolean,
    public ledgerOptionNo: number,
    public goal: number,
    public configActive: boolean,
    public subDes1: string,
    public subDes2: string,
    public subDes3: string,
    public orderNo1: number,
    public orderNo2: number,
    public orderNo3: number,
  ) { };
}

export class DesignationChoice { //This represents a combination of des and sub-des values chosen by the user.
  constructor( //These params will automatically become the properties of this class
    public choiceAmount: string = null,
    public desNo: number = 0,
    public subItemNo1: number = 0,
    public subItemNo2: number = 0,
    public subItemNo3: number = 0,
    public allowPledge: boolean = false,
    public fulfillsPledge: boolean = false,
    public desDetails: GetDesignationDetailsResponse = null,
    public desSelected: Designation = null,
    public subItems1: DesignationSubItem[] = [],
    public subItems2: DesignationSubItem[] = [],
    public subItems3: DesignationSubItem[] = [],
  ) { };
}

export class ContributionDesignation {
  public desNo: number;
  public desConfigNo: number;
  public subItemNo1: number;
  public subItemNo2: number;
  public subItemNo3: number;
  public desAmount: string;
  public fulfillsPledge: boolean;
}

export class GetDesignationsResponse extends ApiResponse {
  public designationList: Designation[];
}

export class DesignationDetails {
  public designation: Designation;
  public subItemList1: DesignationSubItem[];
  public subItemList2: DesignationSubItem[];
  public subItemList3: DesignationSubItem[];
  public configList: DesignationConfig[];
}

export class GetDesignationDetailsResponse extends ApiResponse {
  public subItemList1: DesignationSubItem[];
  public subItemList2: DesignationSubItem[];
  public subItemList3: DesignationSubItem[];
  public configList: DesignationConfig[];
}

export class Donor {
  constructor( //These params will automatically become the properties of this class
    public donorNo: number,
    public firstName: string,
    public lastName: string,
    public address1: string,
    public address2: string,
    public city: string,
    public stateOptionNo: number,
    public state: string,
    public zipCode: string,
    public country: string,
    public phone: string,
    public email: string,
    public envelope: string,
    public hasSSN: boolean,
    public federalID: string,
    public jointDonorNo: number,
    public jointDonorPrimary: boolean,
    public jointDonorName: string,
    public usePaperStatement: boolean,
    public donorActive: boolean,
  ) { };
}

export class GetDonorsResponse extends ApiResponse {
  public donorList: Donor[];
}

export class StatementDonor {
  public donorNo: number;
  public donorName: string;
  public envelope: string;
  public jointDonorName: string;
  public totalCashCont: string;
  public numCashCont: number;
  public numNonCashCont: number;
}

export class GetStatementDonorsResponse extends ApiResponse {
  public donorList: StatementDonor[];
}

export class GivingReportData {
  public datePeriod: string;
  public reportDesignation: string;
  public givingCurrentYear: number;
  public givingPriorYear: number;
  public yearlyIncrease: number;
  public totalGivingForever: number;
  public budgetTotal: number;
  public budgetVariance: number;
  public budgetPercentage: number;
  public pledgeTotal: number;
  public pledgeVariance: number;
  public pledgePercentage: number;
}

export class GivingReportDataResponse extends ApiResponse {
  public reportDataList: GivingReportData[];
}

export class DevReportSummary {
  public dateYear: string;
  public datePeriod: string;
  public giftType: string;
  public desName: string;
  public subDes1: string;
  public subDes2: string;
  public subDes3: string;
  public accountName: string;
  public totalAmount: number;
  public numConts: number;
  public numDonors: number;
  public avgPerContribution: number;
  public avgPerDonor: number;
  public donorName: string;
  public jointDonorName: string;
  public address1: string;
  public address2: string;
  public city: string;
  public state: string;
  public zipCode: string;
  public country: string;
  public phone: string;
  public email: string;
  public envelope: string;
  public usePaperStatement: boolean;
  public donorActive: boolean;
}

export class DevReportSummaryResponse extends ApiResponse {
  public reportDataList: DevReportSummary[];
  public newFilterSql: string;
}

export class DevReportDetails {
  public dateYear: string;
  public datePeriod: string;
  public giftType: string;
  public contDate: string;
  public contOrDesAmount: number;
  public checkNumber: number;
  public desName: string;
  public subDes1: string;
  public subDes2: string;
  public subDes3: string;
  public fulfillsPledge: boolean;
  public accountName: string;
  public donorName: string;
  public jointDonorName: string;
  public address1: string;
  public address2: string;
  public city: string;
  public state: string;
  public zipCode: string;
  public country: string;
  public phone: string;
  public email: string;
  public envelope: string;
  public usePaperStatement: boolean;
  public donorActive: boolean;
}

export class DevReportDetailsResponse extends ApiResponse {
  public reportDataList: DevReportDetails[];
  public newFilterSql: string;
}

export class PledgesReportSummary {
  public dateYear: string;
  public datePeriod: string;
  public giftType: string;
  public desName: string;
  public subDes1: string;
  public subDes2: string;
  public subDes3: string;
  public accountName: string;
  public givingAmount: number;
  public pledgeAmount: number;
  public pledgeVariance: number;
  public pledgePercentage: number;
  public numPledges: number;
  public pledgeAverage: number;
  public numDonors: number;
  public pledgeAveragePerDonor: number;
  public numConts: number;
  public averageCont: number;
  public pledgeCreated: string;
  public donorName: string;
  public jointDonorName: string;
  public address1: string;
  public address2: string;
  public city: string;
  public state: string;
  public zipCode: string;
  public country: string;
  public phone: string;
  public email: string;
  public envelope: string;
  public usePaperStatement: boolean;
  public donorActive: boolean;
}

export class PledgesReportSummaryResponse extends ApiResponse {
  public reportDataList: PledgesReportSummary[];
  public newFilterSql: string;
}

export class PledgesReportDetails {
  public dateYear: string;
  public datePeriod: string;
  public giftType: string;
  public contDate: string;
  public desAmount: number;
  public checkNumber: string;
  public desName: string;
  public subDes1: string;
  public subDes2: string;
  public subDes3: string;
  public accountName: string;
  public totalGiving: number;
  public pledgeAmount: number;
  public pledgeVariance: number;
  public pledgePercentage: number;
  public numConts: number;
  public averageCont: number;
  public pledgeCreated: string;
  public donorName: string;
  public jointDonorName: string;
  public address1: string;
  public address2: string;
  public city: string;
  public state: string;
  public zipCode: string;
  public country: string;
  public phone: string;
  public email: string;
  public envelope: string;
  public usePaperStatement: boolean;
  public donorActive: boolean;
}

export class PledgesReportDetailsResponse extends ApiResponse {
  public reportDataList: PledgesReportDetails[];
  public newFilterSql: string;
}

export class ReportFavorite {
  public reportFavoriteNo: number;
  public nonprofitNo: number;
  public reportTypeNo: number;
  public reportName: string;
  public lastSaved: string;
  public periodNo: string; //The next several properties are ints or datetimes on the server side, but we use them as strings, so we'll let the JSON serialization do its magic
  public reportYear: string;
  public fromDate: string;
  public toDate: string;
  public dateGroupByNo: string;
  public desGroupByNos: number[];
  public donorGroupByNos: number[];
  public giftTypeGroupByNos: number[];
  public ledgerGroupByNos: number[];
  public desNo: number;
  public subDes1No: number;
  public subDes2No: number;
  public subDes3No: number;
  public showPriorYearColumns: boolean;
  public showBudgetColumns: boolean;
  public showPledgeColumns: boolean;
  public showDonorsWithoutConts: boolean;
  public pledgeFulfilledNo: number;
  public activeStatusNo: number;
  public contributionStatusNo: number;
  public filterValue: string;
  public filterSql: string;
  public viewContDetails: boolean;
  public donorNosForAdvancedQuery: string;
}

export class GetReportFavoritesResponse extends ApiResponse {
  public reportFavoriteList: ReportFavorite[];
}

export class Training {
  constructor( //These params will automatically become the properties of this class
    public trainingNo: number,
    public trainingName: string,
    public trainingCategory: string,
    public trainingDesc: string,
    public trainingUrl: string,
    public lastSaved: string
  ) { };
}

export class GetTrainingsResponse extends ApiResponse {
  public trainingList: Training[];
}

export class Nonprofit {
  constructor( //These params will automatically become the properties of this class
    public nonprofitNo: number,
    public nonprofitName: string,
    public loginID: string,
    public accountNumber: string,
    public address1: string,
    public address2: string,
    public city: string,
    public stateOptionNo: number,
    public state: string,
    public zipCode: string,
    public phoneNumber: string,
    public nonprofitActive: boolean,
  ) { };
}

export class GetNonprofitsResponse extends ApiResponse {
  public nonprofitList: Nonprofit[];
  public nonprofitSimpleList: SimpleOption[];
}

export class FilterPledges {
  constructor( //These params will automatically become the properties of this class
    public fromDate: string,
    public toDate: string,
    public desNo: string,
    public donorName: string,
  ) { };
}

export class FilterStatements {
  constructor( //These params will automatically become the properties of this class
    public periodNo: string,
    public year: string,
    public fromDate: string,
    public toDate: string,
    public donorName: string,
    public statementPaperEmail: string,
    public showDonorsWithNoContInDateRange: boolean,
  ) { };
}
